import type { Intent } from '@jet/environment/dispatching';
import { IntentKind } from '@jet-app/music-classical/intents';

/**
 * Helper function to generate a boilerplate intent factory function.
 *
 * @example
 * ```ts
 * const makeChannelPageIntent = makeIntentBuilderFunction<ChannelPageIntent>(IntentKind.CHANNEL_PAGE);
 *  // returns:
 * const makeChannelPageIntent = (options: Omit<ChannelPageIntent, "$kind">): ChannelPageIntent => {
 *    return {
 *       ...options,
 *       $kind: IntentKind.CHANNEL_PAGE,
 *    };
 * };
 * ```
 * @param kind - kind of intent
 * @returns an intent factory function
 */
// tslint:disable-next-line:typedef
export function makeIntentBuilderFunction<I extends Intent<unknown>>(
    kind: IntentKind,
    baseOptions?: any,
) {
    return (options?: Omit<I, '$kind'>): I => {
        return {
            // tslint:disable no-object-literal-type-assertion
            ...baseOptions,
            ...options,
            $kind: kind,
        } as I;
    };
}

/**
 * Helper function to generate boilerplate intent guard.
 *
 * @example
 * ```ts
 *  const isChannelPageIntent = makeIntentGuard<ChannelPageIntent>(IntentKind.CHANNEL_PAGE);
 *  // returns:
 * const isChannelPageIntent = (intent: Intent<unknown>): intent is ChannelPageIntent => {
 *    return intent?.kind === IntentKind.CHANNEL_PAGE;
 * }
 * ```
 * @param kind - kind of intent
 * @returns an intent guard function
 */
// tslint:disable-next-line:typedef
export function makeIntentGuard<I extends Intent<unknown>>(kind: IntentKind) {
    return (intent: Intent<unknown>): intent is I => {
        return intent?.$kind === kind;
    };
}
